import type { CriminalAppellateWizardDraft } from '../../../types/criminalAppellateCaseRequest';
import type { LookupItem } from '../../../mocks/criminalMasterData';
import { contactNumberError, exceedsMax, isTodayOrFutureIsoDate, maxCharLimitExceededError } from '../../../utils/caseFieldValidation';
import { categoryShowsInstituteType, isOtherCorporateInstituteSelected } from '../../../utils/instituteTypeRules';
import { draftRequiresOtherDeliveryText } from '../../../utils/deliveryMethod';
import type { RequiredErrors, RequiredFieldSpec } from '../../../utils/requiredValidation';
import { validateRequired } from '../../../utils/requiredValidation';
import { validationDateFutureOnly, validationDocReasonRequired } from '../../../utils/validationI18n';

export type CriminalAppellateWizardStep = 1 | 2 | 3 | 4 | 5;

export const MAX_DOC_SKIP_REASON = 255;
export const DOC_SKIP_REASON_PLACEHOLDER = 'Enter Reason (Mention if document is not uploaded)';

export function isHighUrgencyLevel(name: string): boolean {
  return name.trim().toLowerCase().includes('high');
}

const MAX_NAME = 255;
const MAX_DESC = 2000;

export function validateCriminalAppellateStep(
  step: CriminalAppellateWizardStep,
  draft: CriminalAppellateWizardDraft,
  fileCategories: LookupItem[] = [],
  corporateInstitutes: LookupItem[] = [],
): { valid: boolean; errors: RequiredErrors; specs: RequiredFieldSpec[] } {
  const specs: RequiredFieldSpec[] = [];
  const extra: RequiredErrors = {};

  if (step >= 1) {
    specs.push(
      { key: 'fileCategoryId', label: 'File Category', value: draft.fileCategoryId, elementId: 'crim-app-fileCategory' },
      { key: 'subFileCategoryId', label: 'Sub File Category', value: draft.subFileCategoryId, elementId: 'crim-app-subFileCategory' },
      { key: 'description', label: 'Description', value: draft.description, elementId: 'capp-description' },
      { key: 'appealMatter', label: 'Appeal Matter', value: draft.appealMatter, elementId: 'capp-appealMatter' },
      { key: 'provinceId', label: 'Province', value: draft.provinceId, elementId: 'capp-province' },
      { key: 'districtId', label: 'District', value: draft.districtId, elementId: 'capp-district' },
      { key: 'yourReferenceNumber', label: 'Your Reference Number (External)', value: draft.yourReferenceNumber, elementId: 'capp-yourRef' },
      { key: 'hasConnectedMatters', label: 'Connected matters', value: draft.hasConnectedMatters, elementId: 'capp-connected' },
    );

    if (draft.appealMatter === 'yes') {
      specs.push(
        { key: 'originalCourt', label: 'Original Court', value: draft.originalCourt, elementId: 'capp-originalCourt' },
        { key: 'judgementOrderFileName', label: 'Judgement/Order', value: draft.judgementOrderFileName, elementId: 'capp-judgementOrder' },
      );
    }

    if (draft.hasConnectedMatters === 'yes') {
      specs.push(
        { key: 'connectedCaseNumber', label: 'Connected Case Number', value: draft.connectedCaseNumber, elementId: 'capp-connectedCase' },
      );
      if (draft.connectedAgdFileRefAvailable) {
        specs.push(
          { key: 'connectedAgdFileRef', label: 'Connected AGD File Reference Number', value: draft.connectedAgdFileRef, elementId: 'capp-connectedAgd' },
        );
      }
    }

    // Institute type is MANDATORY for Criminal Appellate when the category shows it.
    if (categoryShowsInstituteType(draft.fileCategoryId, fileCategories)) {
      specs.push({ key: 'instituteType', label: 'Institute Type', value: draft.instituteType, elementId: 'crim-app-instituteType' });
      if (draft.instituteType === 'corporation') {
        specs.push({ key: 'corporateInstituteId', label: 'Corporate Institute', value: draft.corporateInstitute, elementId: 'crim-app-corporateInstitute' });
        if (isOtherCorporateInstituteSelected(draft.corporateInstitute, corporateInstitutes, draft.corporateInstituteName)) {
          specs.push({ key: 'corporateInstituteOther', label: 'Other Institute Name', value: draft.corporateInstituteOther, elementId: 'crim-app-corporateOther' });
        }
      }
    }

    if (exceedsMax(draft.description, MAX_DESC)) extra.description = maxCharLimitExceededError();
    if (exceedsMax(draft.originalCourt, MAX_NAME)) extra.originalCourt = maxCharLimitExceededError();
    if (exceedsMax(draft.yourReferenceNumber, MAX_NAME)) extra.yourReferenceNumber = maxCharLimitExceededError();
    if (exceedsMax(draft.otherExternalReferences, MAX_NAME)) extra.otherExternalReferences = maxCharLimitExceededError();
    if (exceedsMax(draft.caseNumber, MAX_NAME)) extra.caseNumber = maxCharLimitExceededError();
    if (exceedsMax(draft.connectedCaseNumber, MAX_NAME)) extra.connectedCaseNumber = maxCharLimitExceededError();
    if (exceedsMax(draft.connectedAgdFileRef, MAX_NAME)) extra.connectedAgdFileRef = maxCharLimitExceededError();
    if (exceedsMax(draft.corporateInstituteOther, MAX_NAME)) extra.corporateInstituteOther = maxCharLimitExceededError();
  }

  if (step >= 2) {
    specs.push(
      { key: 'plaintiffApplicantName', label: 'Plaintiff / Applicant Name', value: draft.plaintiffApplicantName, elementId: 'capp-plaintiff' },
      { key: 'defendantRespondentName', label: 'Defendant / Respondent Name', value: draft.defendantRespondentName, elementId: 'capp-defendant' },
      { key: 'clientType', label: 'Select one of the following', value: draft.clientType, elementId: 'capp-clientType' },
      { key: 'legalOfficerName', label: 'Name of the Legal Officer / Officer In-Charge', value: draft.legalOfficerName, elementId: 'capp-legalOfficer' },
      { key: 'legalOfficerContact', label: 'Contact Number', value: draft.legalOfficerContact, elementId: 'capp-legalContact' },
    );

    if (exceedsMax(draft.plaintiffApplicantName, MAX_NAME)) extra.plaintiffApplicantName = maxCharLimitExceededError();
    if (exceedsMax(draft.defendantRespondentName, MAX_NAME)) extra.defendantRespondentName = maxCharLimitExceededError();
    if (exceedsMax(draft.otherInvolvedInstitutions, MAX_DESC)) extra.otherInvolvedInstitutions = maxCharLimitExceededError();
    if (exceedsMax(draft.legalOfficerName, MAX_NAME)) extra.legalOfficerName = maxCharLimitExceededError();

    const contactErr = contactNumberError(draft.legalOfficerContact);
    if (contactErr) extra.legalOfficerContact = contactErr;
  }

  if (step >= 3) {
    specs.push(
      { key: 'urgencyLevelId', label: 'Urgency Level', value: draft.urgencyLevelId, elementId: 'capp-urgency' },
    );

    if (isHighUrgencyLevel(draft.urgencyLevelName)) {
      specs.push({
        key: 'urgencyJustification',
        label: 'Urgency Justification',
        value: draft.urgencyJustification,
        elementId: 'capp-urgencyJustification',
      });
    }

    if (exceedsMax(draft.urgencyJustification, MAX_DESC)) extra.urgencyJustification = maxCharLimitExceededError();
    if (draft.deadline && !isTodayOrFutureIsoDate(draft.deadline)) {
      extra.deadline = validationDateFutureOnly();
    }
  }

  if (step >= 4) {
    specs.push({
      key: 'deliveryMethodIds',
      label: 'Delivery Method',
      value: draft.deliveryMethodIds,
      elementId: 'capp-delivery',
    });
    if (draftRequiresOtherDeliveryText(draft)) {
      specs.push({
        key: 'deliveryOther',
        label: 'Other Delivery Method',
        value: draft.deliveryOther,
        elementId: 'capp-deliveryOther',
      });
    }
  }

  if (step >= 5) {
    const docErrors = validateStep5Documents(draft);
    Object.assign(extra, docErrors);
  }

  const reqErrs = validateRequired(specs);
  const errors = { ...reqErrs, ...extra };
  return { valid: Object.keys(errors).length === 0, errors, specs };
}

/** Step 5 — mandatory documents must be uploaded or have a skip reason (max 255 chars). */
export function validateStep5Documents(draft: CriminalAppellateWizardDraft): RequiredErrors {
  const errors: RequiredErrors = {};
  draft.documents.forEach((doc) => {
    if (doc.fileName) return;
    if (doc.mandatory && !doc.skipReason.trim()) {
      errors[`doc-reason-${doc.id}`] = validationDocReasonRequired();
      return;
    }
    if (exceedsMax(doc.skipReason, MAX_DOC_SKIP_REASON)) {
      errors[`doc-reason-${doc.id}`] = maxCharLimitExceededError();
    }
  });
  return errors;
}
